/**
 * The entry packs on sale, as the candidate server prices them (`GET entries/packs`).
 *
 * The server reads each pack's live Stripe `default_price`, so the amounts here are
 * only ever what it sent: whole minor units (cents), tax added at checkout. When
 * nothing can be sold it sends no packs and a plain `reason`. The reason is kept in
 * the normalized answer, but no page prints it: with nothing to sell, the Entries
 * page and a cycle's enrolment panel leave buying out entirely.
 */

export const PACKS_UNAVAILABLE_REASON = 'Buying entries is not available right now. Please try again later.';

const DEFAULT_TAX_LABEL = 'plus HST';

const isWholeNumber = (value) => Number.isInteger(value) && value >= 0;

const normalizePack = (raw) => {
    if (!raw || typeof raw !== 'object') return null;

    const productId = typeof raw.product_id === 'string' ? raw.product_id.trim() : '';
    const currency = typeof raw.currency === 'string' ? raw.currency.trim().toUpperCase() : '';
    const { entries, amount } = raw;
    if (!productId || !currency || !Number.isInteger(entries) || entries < 1 || !isWholeNumber(amount)) {
        return null;
    }

    const perEntry = Number.isFinite(raw.per_entry_amount) && raw.per_entry_amount >= 0
        ? raw.per_entry_amount
        : Math.round(amount / entries);
    const taxLabel = [raw.tax_label, raw.tax_note].find(label => typeof label === 'string' && label.trim());

    return {
        productId,
        entries,
        amount,
        currency,
        perEntryAmount: perEntry,
        taxLabel: taxLabel ? taxLabel.trim() : DEFAULT_TAX_LABEL,
    };
};

/**
 * The server's answer as the cards need it: the sellable packs, fewest entries
 * first, and the sentence to show when there are none. Takes the documented object
 * or a bare list; a malformed pack is dropped rather than drawn with a wrong price.
 */
export const normalizeEntryPacks = (data) => {
    const list = Array.isArray(data) ? data : Array.isArray(data?.packs) ? data.packs : [];
    const seen = new Set();
    const packs = list
        .map(normalizePack)
        .filter(pack => pack && !seen.has(pack.productId) && seen.add(pack.productId))
        .sort((a, b) => a.entries - b.entries);

    const reason = typeof data?.reason === 'string' && data.reason.trim() ? data.reason.trim() : '';
    return {
        packs,
        reason: packs.length ? '' : reason || PACKS_UNAVAILABLE_REASON,
    };
};

const fractionDigits = (currency) => {
    try {
        return new Intl.NumberFormat('en-CA', { style: 'currency', currency })
            .resolvedOptions().maximumFractionDigits;
    } catch (e) {
        return 2;
    }
};

/** A minor-unit amount as a price tag: 2500 CAD -> "$25.00", 2500 USD -> "US$25.00". */
export const formatPackMoney = (amount, currency) => {
    const digits = fractionDigits(currency);
    const value = amount / 10 ** digits;
    try {
        return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(value);
    } catch (e) {
        return `${value.toFixed(digits)} ${currency}`;
    }
};

/**
 * The pack with the lowest price per entry, marked for the student — but only when
 * there is one clear answer: a tie, a single pack, or packs in different currencies
 * mark nothing.
 */
export const cheapestPerEntryId = (packs) => {
    if (!packs || packs.length < 2) return '';
    if (new Set(packs.map(pack => pack.currency)).size > 1) return '';

    const lowest = Math.min(...packs.map(pack => pack.perEntryAmount));
    const cheapest = packs.filter(pack => pack.perEntryAmount === lowest);
    return cheapest.length === 1 ? cheapest[0].productId : '';
};

export const entriesLabel = (count) => `${count} ${count === 1 ? 'entry' : 'entries'}`;
